import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import { useSelector, useDispatch } from "react-redux";
import { useState } from "react";
import PositiveOrNegetiveSTSkeleton from "../../../../../SearchTermSkeletons/PositiveOrNegetiveSTSkeleton";
import {
  setPositiveKeywordDefinitions,
  removePositiveDefinition,
} from "../../../../../../../../../features/aiRecomendationTabSlice";
import { formatDate } from "../../../../../../../../../utils/helpers";

const SecondPositiveST = ({ open, rowData, onClose }: any) => {
  const dispatch = useDispatch();
  const {
    positiveSTLoader,
    keywordDetails,
    selectedPositiveKeywordDefinitions,
  } = useSelector((state: any) => state.aiRecomandationTab);

  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Map keywordDetails to rows
  const rows =
    keywordDetails?.map((item: any, index: number) => ({
      id: index + 1,
      keyword_shared_uid: item.keyword_shared_Uid,
      keyword_id: item.keyword_id,
      keywordName: item.keyword_name || "",
      matchType: item.keyword_match_type || "",
      linkType: item.link_type || "",
      adgroupName: item.adgroup_name || "",
      adgroup_shared_uid: item.adgroup_shared_uid,
      campaignName: item.campaign_name || "",
      imp: item.impression_last_30_days ?? 0,
      clicks: item.clicks_last_30_days ?? 0,
      cost: `${item.cost_last_30_days ?? 0}$`,
      cvr: `${Math.round(Number(item.conversions_last_30_days ?? 0)) * 100}%`,
      last_seen: formatDate(item.last_seen) || " ",
      search_term_shared_uid: item.search_term_shared_uid,
    })) || [];

  // Get definition options based on link type
  const getDefinitionOptions = (
    linkType: string,
    rowKeywordName: string,
    searchTerm?: string
  ) => {
    const options = [];

    if (linkType === "Allowed") {
      options.push({ label: "Set as Exclusive", value: "exclusive" });
    } else if (linkType === "Exclusive") {
      options.push({ label: "Set as Allow", value: "allow" });
    }

    if (
      !searchTerm ||
      rowKeywordName.trim().toLowerCase() !== searchTerm.trim().toLowerCase()
    ) {
      options.push({ label: "Negative List", value: "negative" });
    }

    return options;
  };

  // Define columns
  const columns: GridColDef[] = [
    {
      field: "keywordName",
      headerName: "Keyword Name",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "matchType",
      headerName: "Match Type",
      flex: 0.6,
      minWidth: 110,
    },
    {
      field: "linkType",
      headerName: "Link Type",
      flex: 0.6,
      minWidth: 110,
    },
    {
      field: "adgroupName",
      headerName: "Adgroup Name",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "campaignName",
      headerName: "Campaign Name",
      flex: 1,
      minWidth: 220,
    },
    {
      field: "last_seen",
      headerName: "Last Seen",
      flex: 0.6,
      minWidth: 110,
    },
    { field: "imp", headerName: "IMP", flex: 0.6, minWidth: 110 },
    { field: "clicks", headerName: "Clicks", flex: 0.6, minWidth: 110 },
    { field: "cost", headerName: "Cost", flex: 0.6, minWidth: 110 },
    { field: "cvr", headerName: "CVR", flex: 0.6, minWidth: 110 },
    {
      field: "definition",
      headerName: "Definition",
      flex: 0.8,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => {
        const linkType = params.row.linkType;
        const options = getDefinitionOptions(
          linkType,
          params.row.keywordName,
          rowData?.search_term
        );

        // Create a composite key: search_term + keyword_shared_uid
        const keywordSharedUid = params.row.keyword_shared_uid || params.row.id;
        const compositeKey = `${rowData?.search_term}_${keywordSharedUid}`;

        // Get the current selected value from Redux state using composite key
        const selectedDefinition =
          selectedPositiveKeywordDefinitions?.[compositeKey]?.definition;
        const selectedOption = options.find(
          (opt) => opt.value === selectedDefinition
        );

        return (
          <Autocomplete
            size="small"
            options={options}
            value={selectedOption || null}
            getOptionLabel={(option) => option.label}
            onChange={(_, selectedOption) => {
              if (selectedOption) {
                // Set the definition when an option is selected
                dispatch(
                  setPositiveKeywordDefinitions({
                    id: compositeKey, // Use composite key
                    row: {
                      ...params.row,
                      search_term: rowData?.search_term,
                      search_term_match_type: rowData?.search_term_match_type,
                    },
                    definition: selectedOption.value,
                  })
                );
              } else {
                // Remove the definition when cleared
                dispatch(removePositiveDefinition(compositeKey));
              }
            }}
            sx={{ width: "100%" }}
            renderInput={(paramsInput) => (
              <TextField
                {...paramsInput}
                placeholder="Select..."
                variant="standard"
                color="secondary"
                InputProps={{
                  ...paramsInput.InputProps,
                  disableUnderline: true,
                }}
                data-tracker="615"
              />
            )}
          />
        );
      },
    },
  ];

  if (!rowData) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason !== "backdropClick") {
            onClose();
          }
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{rowData?.search_term} - Positive Keywords</DialogTitle>
        <DialogContent>
          {positiveSTLoader ? (
            <PositiveOrNegetiveSTSkeleton />
          ) : (
            <Box
              sx={{
                width: "100%",
                mt: 1,
                height: `${Math.min(rows.length * 52 + 56, 500)}px`,
              }}
            >
              <DataGridPro
                sx={{
                  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                    {
                      outline: "none",
                    },
                  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                    { outline: "none" },
                  "& .MuiDataGrid-cellEmpty": {
                    width: "auto",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: "#fff",
                  },
                  "& .MuiDataGrid-row.Mui-selected": {
                    backgroundColor: "rgba(156, 39, 176, 0.08) !important",
                    "&:hover": {
                      backgroundColor: "rgba(156, 39, 176, 0.16) !important",
                    },
                  },
                  "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedLeft":
                    {
                      backgroundColor: "#f0def3 !important",
                    },
                  "& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedLeft":
                    {
                      backgroundColor: "#e6d6e9ff !important",
                    },
                  "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedRight":
                    {
                      backgroundColor: "#f0def3 !important",
                    },
                  "& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedRight":
                    {
                      backgroundColor: "#e6d6e9ff !important",
                    },
                  "& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight":
                    {
                      backgroundColor: "#ffffff",
                    },
                  "& .MuiDataGrid-cell.Mui-selected": {
                    backgroundColor: "inherit !important",
                  },
                  "& .MuiCheckbox-root": {
                    color: "#9c27b0 !important",
                  },
                  "& .MuiCheckbox-root.Mui-checked": {
                    color: "#9c27b0",
                  },
                  "& .MuiDataGrid-virtualScroller": {
                    minHeight: "52px !important",
                  },
                  "& .MuiDataGrid-footerContainer": {
                    minHeight: "auto !important",
                  },
                  border: "none",
                }}
                autoHeight
                density="compact"
                rows={positiveSTLoader ? [] : rows}
                columns={columns}
                pagination
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50, 100]}
                disableColumnMenu
                disableColumnFilter
                disableColumnReorder
                sortingOrder={["asc", "desc"]}
                isCellEditable={() => false}
                disableRowSelectionOnClick
                initialState={{
                  pinnedColumns: {
                    left: ["keywordName", "matchType"],
                    right: ["definition"],
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "16px 20px 16px" }}>
          <Button onClick={onClose} color="secondary" variant="outlined">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SecondPositiveST;
